import { FieldValues, UseFormReturn } from "react-hook-form";
import { useCreateRecord } from "../../../api/useCreateRecord";
import { useStore } from "../../../store/store";
import { FormatedField } from "../../../types/fields.types";
import { sanitizeData } from "../../../utils/sanitizeFormData";
import { ServicePrescribed, TreatmentPrescription, TreatmentPrescriptionForm } from "./types";
import { validateDuplicatedServices, validateIfServiceSelected, validateServiceAndGoalsMatch } from "./utils";
import { useUpdateRecordAndRelatedRecord } from "../../../api/useUpdateEntityAndRelatedEntity";
import { useQueryClient } from "@tanstack/react-query";

export const useServicesPrescribedFormHandler = (
      entityForm: UseFormReturn<FieldValues>,
      treatmentPrescription: TreatmentPrescription,
      fieldsTreatmentPrescription: Array<FormatedField>,
      fieldsServicesPrescribed: Array<FormatedField>,
      treatmentTherapyGoals: Array<any>
) => {
    const { notifications, appDialog } = useStore();
    const queryClient = useQueryClient();
    const createRecord = useCreateRecord({ queryInvalidatePrefix: "treatment_prescription" });
    const updateRecordAndRelatedRecord = useUpdateRecordAndRelatedRecord({ queryInvalidatePrefix: "treatment_prescription" });

     const submitFn = async (formData: TreatmentPrescriptionForm) => {
        const isServicesPrescribedSelected = validateIfServiceSelected(formData?.services_prescribed, notifications);
        if (!isServicesPrescribedSelected) return;
    
        const isServicesDuplicated = validateDuplicatedServices(formData.services_prescribed, notifications);
        if (!isServicesDuplicated) return;

        const cleanedGoalsData = cleanGoalsField(formData);
        
        const isCorrectServiceAndGoalsMatch = validateServiceAndGoalsMatch(cleanedGoalsData.services_prescribed, treatmentTherapyGoals, notifications);
        if (!isCorrectServiceAndGoalsMatch) return;

        if (!treatmentPrescription) {
          const cleanedServicesData = cleanServiceField(cleanedGoalsData);
          const sanitizedData = sanitizeData(cleanedServicesData, fieldsTreatmentPrescription);

          try {
            await createRecord.mutateAsync({
              entity: "treatment_prescription",
              data: sanitizedData,
            });
            
            const treatmentId = sanitizedData.treatment;
            queryClient.invalidateQueries({
              predicate: (query: any) => {
                return query.queryKey[0].startsWith(`treatments-${treatmentId}-`);
              },
            });
            
            notifications.setNotification({
              isOpen: true,
              message: "Prescrição criada com sucesso.",
              severity: "success",
            });
          } catch (error) {
            notifications.setNotification({
              isOpen: true,
              message: "Erro ao criar prescrição.",
              severity: "error",
            });
          }
          appDialog.toggleIsOpen();
          return;
        }

        updateRecordAndRelatedRecord<TreatmentPrescription, ServicePrescribed>(
          "treatment_prescription",
          cleanedGoalsData,
          treatmentPrescription,
          fieldsTreatmentPrescription,
          "services_prescribed",
          fieldsServicesPrescribed,
          entityForm,
			);

        appDialog.toggleIsOpen();
      };
   return {submitFn} 
}

const cleanServiceField = (formData: TreatmentPrescriptionForm) => {
  const servicesPrescribed = formData.services_prescribed.map((item) => ({
    ...item,
    service: item.service.id,
    estimated_total_sessions: item.estimated_total_sessions || null,
  }));
  return {
    ...formData,
    services_prescribed: servicesPrescribed,
  };
};

const cleanGoalsField = (formData: any) => {
  const { multiple_goals, ...rest } = formData;

  if (!rest.services_prescribed) {
    return rest;
  }

  const servicesPrescribed = rest.services_prescribed.map((item: any) => ({
    ...item,
    goals: item.goals?.map((goal: any) => ({
      treatment_therapy_goals_id: goal.id ?? goal.treatment_therapy_goals_id,
    })).filter((goal: any) => goal.treatment_therapy_goals_id !== undefined),
  }));

  return {
    ...rest,
    services_prescribed: servicesPrescribed,
  };
};